import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { 
  EyeIcon, 
  HeartIcon, 
  ShareIcon,
  CalendarDaysIcon,
  UserIcon,
  TagIcon,
  XMarkIcon,
  Bars3Icon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { PublicPost, PostCard, RelatedCourseCard } from '../../types/blog';
import { BlogApi } from '../../services/blogApi';
import Breadcrumb from '../../components/blog/Breadcrumb';
import ArticleCard from '../../components/blog/ArticleCard';
// ...existing code...
import SEOHead from '../../components/common/SEOHead';
import { FolderOpenIcon } from '@heroicons/react/24/solid';

const formatCourseFee = (fee?: number | null) => {
  if (fee == null || Number.isNaN(Number(fee))) return null;
  return `${Number(fee).toLocaleString('vi-VN')}đ`;
};

const RelatedCoursesCta = ({
  quote,
  courses,
}: {
  quote?: string | null;
  courses: RelatedCourseCard[];
}) => {
  if (!courses.length) return null;
  return (
    <section className="mt-8 rounded-eh border border-eh-border bg-eh-sage/30 p-5 lg:p-6">
      <p className="eh-body text-base text-eh-forest-ink mb-4">
        {quote?.trim() ||
          'Muốn tìm hiểu thêm về chủ đề này thì tham khảo khóa học sau'}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {courses.slice(0, 2).map((course) => (
          <Link
            key={course.id}
            to={`/courses/${course.hashId}`}
            className="group flex gap-3 rounded-eh border border-eh-border bg-m2-surface-lowest p-3 shadow-eh-soft transition hover:border-m2-primary/40 hover:shadow-md"
          >
            {course.imageUrl ? (
              <img
                src={course.imageUrl}
                alt=""
                className="h-20 w-28 shrink-0 rounded-md object-cover"
              />
            ) : (
              <div className="flex h-20 w-28 shrink-0 items-center justify-center rounded-md bg-m2-surface-low text-xs text-m2-on-surface-variant">
                Course
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-m2-on-surface group-hover:text-m2-primary line-clamp-2">
                {course.name}
              </h3>
              {course.shortDescription ? (
                <p className="mt-1 text-xs text-m2-on-surface-variant line-clamp-2">
                  {course.shortDescription.replace(/<[^>]*>/g, ' ').trim()}
                </p>
              ) : null}
              <div className="mt-2 flex items-center justify-between gap-2">
                {formatCourseFee(course.fee) ? (
                  <span className="text-sm font-semibold text-eh-forest-ink">
                    {formatCourseFee(course.fee)}
                  </span>
                ) : (
                  <span />
                )}
                <span className="text-xs font-medium text-m2-primary">Đăng ký →</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

interface SharePlatform {
  name: string;
  icon: React.ReactNode;
  shareUrl: (url: string, title: string) => string;
  platform: 'facebook' | 'linkedin' | 'zalo';
}

const sharePlatforms: SharePlatform[] = [
  {
    name: 'Facebook',
    icon: <span className="text-m2-primary">FB</span>,
  shareUrl: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    platform: 'facebook'
  },
  {
    name: 'LinkedIn', 
    icon: <span className="text-m2-primary">linkedin</span>,
  shareUrl: (url) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    platform: 'linkedin'
  },
  {
    name: 'Zalo',
    icon: <span className="text-m2-primary">Zalo</span>,
  shareUrl: (url) => `https://zalo.me/share.html?url=${encodeURIComponent(url)}`,
    platform: 'zalo'
  }
];

const ArticleDetailPage: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const [post, setPost] = useState<PublicPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<PostCard[]>([]);
  const [topicPosts, setTopicPosts] = useState<PostCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [liked, setLiked] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);
  const [shareLoading, setShareLoading] = useState<string | null>(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [brokenImages, _] = useState<string[]>([]);

  // Function to process HTML content to convert plain text URLs to clickable links
  const processHtmlContent = (htmlContent: string): string => {
    // First, add target="_blank" and styling classes to existing links
    let processedContent = htmlContent.replace(
      /<a\s+([^>]*?)href="([^"]*)"([^>]*?)>/gi,
      '<a class="text-m2-primary hover:text-m2-primary underline" $1href="$2"$3 target="_blank" rel="noopener noreferrer">'
    );

    // Also handle links that might not have href first
    processedContent = processedContent.replace(
      /<a\s+([^>]*?)(?!.*target=)>/gi,
      '<a class="text-m2-primary hover:text-m2-primary underline" $1 target="_blank" rel="noopener noreferrer">'
    );


    // Convert plain text URLs to clickable links
    // This regex matches URLs that are not already inside <a> tags or href attributes
    const urlRegex = /(^|[^"'>])(https?:\/\/[^\s<>"]+)/gi;

    processedContent = processedContent.replace(urlRegex, (fullMatch, prefix, url) => {
      // Check if this URL is already inside an <a> tag or href attribute
      const beforeUrl = processedContent.substring(0, processedContent.indexOf(fullMatch));
      const openTagIndex = beforeUrl.lastIndexOf('<a');
      const closeTagIndex = beforeUrl.lastIndexOf('</a>');
      const hrefIndex = beforeUrl.lastIndexOf('href=');

      // If we're inside an <a> tag or this is part of an href, don't modify
      if (openTagIndex > closeTagIndex || (hrefIndex > -1 && hrefIndex > closeTagIndex)) {
        return fullMatch;
      }

      // Clean the URL (remove trailing punctuation that shouldn't be part of the link)
      let cleanUrl = url;
      let trailingPunctuation = '';

      const punctuationMatch = url.match(/^(.+?)([.,;!?]+)$/);
      if (punctuationMatch) {
        cleanUrl = punctuationMatch[1];
        trailingPunctuation = punctuationMatch[2];
      }

      return `${prefix}<a class="text-primary-main hover:text-primary-dark" href="${cleanUrl}" target="_blank" rel="noopener noreferrer">${cleanUrl}</a>${trailingPunctuation}`;
    });

    return processedContent;
  };

  // Get current page URL for sharing - memoize to prevent re-renders
  const currentUrl = React.useMemo(() => window.location.href, [uuid]);

  // Simple state reset when UUID changes
  useEffect(() => {
    console.log(`UUID changed to: ${uuid}`);
    if (uuid) {
      setLoading(true);
      setError(null);
      setPost(null);
    }
  }, [uuid]);

  useEffect(() => {
    if (!uuid) return;
    
    let isMounted = true;
    
    const fetchPost = async () => {
      try {
        console.log(`Fetching post data for UUID: ${uuid}`);
        
        // Fetch the main post
        const postData = await BlogApi.getPost(uuid);
        
        if (!isMounted) return;
        
        setPost(postData);
        console.log(`Post data loaded successfully for: ${postData.title}`);

        // Fetch related posts from the same topic (simplified)
        if (postData.topic.slug && isMounted) {
          try {
            const { posts } = await BlogApi.getPostsByTopic(postData.topic.slug, {
                page: 1,
                limit: 10,
                sort: 'newest'
              });

            if (!isMounted) return;

            // Filter out current post
            const otherPosts = posts.filter(p => p.uuid !== uuid);
            setRelatedPosts(otherPosts.slice(0, 5));
            setTopicPosts(otherPosts);
            console.log(`Related posts loaded: ${otherPosts.length} posts`);
          } catch (err) {
            console.error('Error fetching related posts:', err);
            // Don't fail the main post loading if related posts fail
          }
        }

      } catch (err) {
        if (isMounted) {
          console.error('Error fetching post:', err);
          setError(err instanceof Error ? err.message : 'Failed to load article');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPost();

    // Cleanup
    return () => {
      console.log(`Cleanup for UUID: ${uuid}`);
      isMounted = false;
    };
  }, [uuid]);

  // Scroll to top when UUID changes (when navigating to new post)
  useEffect(() => {
    if (uuid && !loading) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [uuid, loading]);

  const handleLike = async () => {
    if (!uuid || likeLoading) return;
    
    try {
      setLikeLoading(true);
      const result = await BlogApi.likePost(uuid);
      
      // Update the post with new like count
      if (post) {
        setPost({
          ...post,
          totalLike: result.totalLikes
        });
      }
      
      setLiked(true);
      // Reset liked state after animation
      setTimeout(() => setLiked(false), 1000);
      
    } catch (err) {
      console.error('Error liking post:', err);
    } finally {
      setLikeLoading(false);
    }
  };

  const handleShare = async (platform: SharePlatform) => {
    if (!uuid || !post) return;
    
    try {
      setShareLoading(platform.platform);
      
      // Open share window
      const shareUrl = platform.shareUrl(currentUrl, post.title);
      window.open(shareUrl, '_blank', 'width=600,height=400');
      
      // Record the share
      const result = await BlogApi.sharePost(uuid, platform.platform);
      
      // Update the post with new share count
      setPost({
        ...post,
        totalShare: result.totalShares
      });
      
    } catch (err) {
      console.error('Error sharing post:', err);
    } finally {
      setShareLoading(null);
    }
  };

  // ...existing code...

  if (!uuid) {
    return <Navigate to="/blog" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-m2-surface-low flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-m2-primary mx-auto"></div>
          <p className="mt-4 text-m2-on-surface-variant">
            {uuid ? `Loading article ${uuid.substring(0, 20)}...` : 'Loading article...'}
          </p>
          <p className="mt-2 text-sm text-m2-on-surface-variant">
            Please wait while we fetch the content
          </p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-m2-surface-low flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-red-600 mb-4">{error || 'Không tìm thấy bài viết.'}</p>
          <Link to="/blog" className="text-m2-primary hover:underline">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Blog', href: '/blog' },
    { label: post.title }
  ];

  // Generate absolute URLs for social media sharing
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://m2mba.com';
  const absoluteUrl = `${siteUrl}/blog/articles/${post.uuid}`;
  const absoluteImageUrl = post.openGraphImageUrl || post.avatarUrl;

  // Create structured data for article
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.metaDescription || post.shortDescription,
    "image": absoluteImageUrl,
    "author": {
      "@type": "Person",
      "name": post.author || "M2MBA Academy"
    },
    "publisher": {
      "@type": "Organization",
      "name": "M2MBA Training Center",
      "logo": {
        "@type": "ImageObject",
        "url": "https://storage.googleapis.com/m2mba/f4872d86-57b4-4bb8-9492-f14a60f22280.jpg"
      }
    },
    "datePublished": new Date(post.createdAt).toISOString(),
    "dateModified": new Date(post.createdAt || post.createdAt).toISOString(),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": absoluteUrl
    },
    "articleSection": post.topic.title,
    "keywords": post.tagNames?.join(', ') || '',
    "url": absoluteUrl
  };

  return (
    <div className="min-h-screen bg-m2-background">
      {/* SEO Meta Tags */}
      <SEOHead
        title={post.metaTitle || `${post.title} - M2MBA Training Center`}
        description={post.metaDescription || post.shortDescription}
        image={absoluteImageUrl}
        url={absoluteUrl}
        type="article"
        author={post.author}
        publishedTime={new Date(post.createdAt).toISOString()}
        modifiedTime={new Date(post.createdAt || post.createdAt).toISOString()}
        section={post.topic.title}
        tags={post.tagNames}
        keywords={post.tagNames?.join(', ')}
        noindex={false}
        structuredData={articleStructuredData}
      />
      
      {/* Breadcrumb */}
      <div className="bg-m2-surface-lowest border-b border-eh-border">
        <div className="eh-container py-3">
          <Breadcrumb items={breadcrumbItems} />
        </div>
      </div>
      <div className="eh-container pt-4 pb-10">
        <div className="flex flex-col lg:flex-row gap-2">
          
          {/* Left Sidebar - Topic Articles */}
          {topicPosts.length > 0 && sidebarExpanded && (
            <div className="lg:w-80 order-2 lg:order-1">
              <div className="bg-m2-surface-lowest rounded-eh border border-eh-border shadow-eh-soft p-6 sticky top-2">
                <div className="relative mb-4">
                    <h3 className="flex items-center bg-eh-sage text-eh-forest-ink font-semibold py-3 pl-4 w-full rounded-eh">
                      <FolderOpenIcon className="h-5 w-5 mr-2" />
                      <span>{post.topic.title}</span>
                    </h3>
                     <button
                        onClick={() => setSidebarExpanded(false)}
                        className="hidden lg:flex items-center justify-center w-8 h-8 rounded-full hover:bg-m2-surface-container/20 transition-colors group absolute top-1/2 -translate-y-1/2 right-2"
                        title="Hide sidebar"
                  >
                    <XMarkIcon className="h-4 w-4 text-eh-forest-ink" />
                    </button>
                </div>
                
                <div className="space-y-4">
                  {topicPosts.slice(0, 5).map((topicPost) => (
                    <div key={topicPost.uuid} className="group">
                      <a
                        href={`/blog/articles/${topicPost.uuid}`}
                        onClick={(e) => {
                          e.preventDefault();
                          console.log(`Navigating to post: ${topicPost.uuid}`);
                          // Force page refresh for now to avoid state issues
                          window.location.href = `/blog/articles/${topicPost.uuid}`;
                        }}
                        className={`block p-3 rounded-md transition-colors ${
                          topicPost.uuid === uuid
                            ? 'bg-m2-primary/10 border border-m2-primary/30'
                            : 'hover:bg-m2-surface-low'
                        }`}
                      >
                        <h4 className={`font-medium text-sm ${
                          topicPost.uuid === uuid ? 'text-m2-on-surface' : 'text-m2-on-surface'
                        }`}>
                          {topicPost.title}
                        </h4>
                        <div className="flex items-center mt-1 text-xs text-m2-on-surface-variant">
                          <EyeIcon className="h-3 w-3 mr-1" />
                          {topicPost.totalView} views
                        </div>
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Toggle Button when Sidebar is Hidden */}
          {topicPosts.length > 0 && !sidebarExpanded && (
            <div className="hidden lg:block fixed left-4 top-1/2 transform -translate-y-1/2 z-10">
              <button
                onClick={() => setSidebarExpanded(true)}
                className="flex items-center justify-center w-10 h-10 bg-m2-surface-container rounded-full shadow-lg hover:shadow-xl transition-all group border border-m2-outline-variant"
                title="Show related articles"
              >
                <Bars3Icon className="h-5 w-5 text-m2-on-surface-variant group-hover:text-m2-primary" />
              </button>
            </div>
          )}

          {/* Main Content */}
          <div className={`flex-1 order-1 ${sidebarExpanded && topicPosts.length > 0 ? 'lg:order-2' : ''}`}>
            <article key={uuid} className="bg-m2-surface-lowest rounded-eh border border-eh-border shadow-eh-soft overflow-hidden">
              
              {/* Article Header */}
              <div className="p-6 lg:p-8 pb-4 lg:pb-5 border-b">
                <div className="flex items-center gap-2 mb-4">
                  <Link
                    to={`/blog/topics/${post.topic.slug}`}
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-m2-primary/15 text-m2-primary hover:bg-m2-primary/20 transition-colors"
                  >
                    <TagIcon className="h-3 w-3 mr-1" />
                    {post.topic.title}
                  </Link>
                  {post.isFeatured && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      Featured
                    </span>
                  )}
                </div>

                <h1 className="eh-display text-4xl lg:text-5xl mb-4">
                  {post.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 text-sm text-m2-on-surface-variant mb-3">
                  <div className="flex items-center">
                    <UserIcon className="h-4 w-4 mr-1" />
                    {post.author}
                  </div>
                  <div className="flex items-center">
                    <CalendarDaysIcon className="h-4 w-4 mr-1" />
                    {new Date(post.createdAt).toLocaleDateString()}
                  </div>
                  <div className="flex items-center">
                    <EyeIcon className="h-4 w-4 mr-1" />
                    {post.totalView} views
                  </div>
                </div>

                {/* Social Engagement */}
                <div className="flex items-center justify-between py-0 border-t">
                  {/* Like Button */}
                  <button
                    onClick={handleLike}
                    disabled={likeLoading}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                      liked || likeLoading
                        ? 'bg-m2-error/10 text-red-700 border border-m2-error/30'
                        : 'bg-m2-surface-low text-m2-on-surface hover:bg-m2-surface-high border border-m2-outline-variant'
                    }`}
                  >
                    {liked ? (
                      <HeartIconSolid className={`h-4 w-4 ${liked ? 'animate-pulse' : ''}`} />
                    ) : (
                      <HeartIcon className="h-4 w-4" />
                    )}
                    {post.totalLike || 0}
                    {likeLoading && <span className="text-xs">...</span>}
                  </button>

                  {/* Share Section - More Compact */}
                  <div className="flex items-center gap-2">
                    <ShareIcon className="h-4 w-4 text-m2-on-surface-variant" />
                    <span className="text-xs text-m2-on-surface-variant">Share:</span>
                    <div className="flex items-center gap-2">
                      {sharePlatforms.map((platform) => (
                        <button
                          key={platform.platform}
                          onClick={() => handleShare(platform)}
                          disabled={shareLoading === platform.platform}
                          className="px-2 py-1.5 flex items-center justify-center min-w-[32px] h-7 rounded text-xs font-medium bg-m2-surface-low text-m2-on-surface hover:bg-m2-surface-high border border-m2-outline-variant transition-colors"
                          title={`Share on ${platform.name}`}
                        >
                          {platform.icon}
                          {shareLoading === platform.platform && <span className="ml-0.5 text-xs">...</span>}
                        </button>
                      ))}
                    </div>
                    <span className="text-xs text-m2-on-surface-variant">({post.totalShare || 0})</span>
                  </div>
                </div>
              </div>

              {/* Article Content */}
              <div className="p-6 lg:p-8 pt-3 lg:pt-4">
                {/* Display warning if broken images detected */}
                {brokenImages.length > 0 && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-800">
                          Some images in this article may not display correctly due to external link restrictions. Placeholders have been shown instead.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Use processed HTML content with clickable URLs */}
                <div
                  className="prose prose-lg max-w-none text-m2-on-surface
                    prose-headings:text-m2-on-surface
                    prose-p:text-m2-on-surface
                    prose-li:text-m2-on-surface
                    prose-strong:text-m2-on-surface
                    prose-a:text-m2-primary prose-a:underline
                    prose-blockquote:text-m2-on-surface prose-blockquote:border-l-m2-primary
                    [&_*]:!text-inherit
                    [&_a]:!text-m2-primary
                    [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:shadow-md [&_img]:my-4
                    [&_table]:border-collapse [&_table]:border [&_table]:border-m2-outline [&_table]:w-full [&_table]:my-4
                    [&_table_th]:border [&_table_th]:border-m2-outline [&_table_th]:bg-m2-surface-low [&_table_th]:px-4 [&_table_th]:py-2 [&_table_th]:text-left [&_table_th]:font-semibold
                    [&_table_td]:border [&_table_td]:border-m2-outline [&_table_td]:px-4 [&_table_td]:py-2
                    [&_table_tbody_tr:nth-child(even)]:bg-m2-surface-low
                    [&_figure]:my-6 [&_figure_img]:mx-auto
                    [&_blockquote]:border-l-4 [&_blockquote]:border-m2-primary [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-4
                    [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-4
                    [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-4
                    [&_li]:my-1"
                  dangerouslySetInnerHTML={{ __html: processHtmlContent(post.postContent) }}
                />
                
                {/* SafeHtmlContent temporarily disabled for debugging */}
                {/* 
                <SafeHtmlContent
                  htmlContent={post.postContent}
                  onBrokenImagesDetected={handleBrokenImagesDetected}
                  className="prose prose-lg max-w-none prose-blue
                    [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:shadow-md [&_img]:my-4
                    [&_table]:border-collapse [&_table]:border [&_table]:border-m2-outline [&_table]:w-full [&_table]:my-4
                    [&_table_th]:border [&_table_th]:border-m2-outline [&_table_th]:bg-m2-surface-low [&_table_th]:px-4 [&_table_th]:py-2 [&_table_th]:text-left [&_table_th]:font-semibold
                    [&_table_td]:border [&_table_td]:border-m2-outline [&_table_td]:px-4 [&_table_td]:py-2
                    [&_table_tbody_tr:nth-child(even)]:bg-m2-surface-low
                    [&_figure]:my-6 [&_figure_img]:mx-auto
                    [&_blockquote]:border-l-4 [&_blockquote]:border-m2-primary [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-4"
                />
                */}
                 {/* Tags Section */}
                 {post.tagNames && post.tagNames.length > 0 && (
                  <div className="px-0 pb-2 border-t mt-6 pt-6">
                    <h3 className="text-sm font-semibold text-m2-on-surface mb-3 flex items-center">
                      <TagIcon className="h-4 w-4 mr-1" /> Tags:
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {post.tagNames.map((tag) => (
                        <Link
                          key={tag}
                          to={`/blog?search=${encodeURIComponent(tag)}`}
                          className="inline-flex items-center px-3 py-1 rounded-eh-pill text-xs font-medium bg-eh-sage text-eh-forest-ink hover:bg-eh-keylime transition-colors"
                        >
                          #{tag}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                <RelatedCoursesCta
                  quote={post.relatedCourseQuote}
                  courses={post.relatedCourses || []}
                />

              </div>
            </article>

            {/* Related Articles */}
            {relatedPosts.length > 0 && (
              <section className="mt-12">
                <h2 className="text-2xl font-bold text-m2-on-surface mb-6">Related Articles</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {relatedPosts.map((relatedPost) => (
                    <ArticleCard
                      key={relatedPost.uuid}
                      post={relatedPost}
                      className="h-full"
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleDetailPage;